"use client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { navItems } from "@/lib/interface";
import { useAuth, logout, isUserProfileComplete } from "@/lib/auth";
import LoginFlowModal from "@/components/login/LoginFlowModal";

interface DynamicNavItemsProps {
  cityPath: string;
  className?: string;
  isMobile?: boolean;
  onItemClick?: () => void;
  onLoginClick?: () => void;
}

const DynamicNavItems = ({
  cityPath,
  className = "",
  isMobile = false,
  onItemClick,
  onLoginClick,
}: DynamicNavItemsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isLoggedIn } = useAuth();

  // Mark as hydrated after first render
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleLogout = async () => {
    try {
      await logout({ 
        redirectTo: '/',
        showConfirmation: true 
      });
      onItemClick?.();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoggedIn) {
      // Check if user has complete profile
      const profileComplete = isUserProfileComplete();
      if (profileComplete) {
        // Redirect to profile page with source=header to indicate coming from header
        try {
          const userData = localStorage.getItem('user_data');
          if (userData) {
            const parsedUserData = JSON.parse(userData);
            if (parsedUserData._id) {
              // Close mobile menu if open
              onItemClick?.();
              window.location.href = `/userProfile/${parsedUserData._id}?source=header`;
              return;
            }
          }
        } catch (error) {
          console.error('Error getting user data:', error);
        }
      }
      // If profile incomplete, show completion form
      if (isMobile && onLoginClick) {
        onLoginClick();
      } else {
        setIsModalOpen(true);
      }
    } else {
      // Not logged in, show login modal
      if (isMobile && onLoginClick) {
        onLoginClick();
      } else {
        setIsModalOpen(true);
      }
    }
    
    onItemClick?.();
  };

  const getAuthButtonText = () => {
    if (isLoggedIn) {
      const profileComplete = isUserProfileComplete();
      return profileComplete ? "PROFILE" : "COMPLETE PROFILE";
    }
    return "LOGIN";
  };

  return (
    <>
      <div className={`flex ${isMobile ? "flex-col" : "md:gap-8 lg:gap-6"}`}>
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            onClick={onItemClick}
            className={`${isMobile ? "block py-2 border-b border-gray-200 " : ""}text-sm font-medium ${
              cityPath === item.href
                ? "!text-[#F05A24]"
                : "text-gray-500 hover:!text-[#F05A24]"
            } ${className}`}
          >
            {item.label}
          </Link>
        ))}

        {/* Auth item - only render after hydration */}
        {isHydrated ? (
          isLoggedIn ? (
            // Show profile/logout options when logged in
            <div className="flex flex-col">
              <button
                onClick={handleLoginClick}
                className={`${
                  isMobile ? "block py-2 text-left border-b border-gray-200 " : ""
                }text-sm font-medium cursor-pointer text-gray-500 hover:!text-[#F05A24] ${className}`}
              >
                {getAuthButtonText()}
              </button>
              {isMobile && (
                <button
                  onClick={handleLogout}
                  className={`block py-2 text-sm text-left font-medium cursor-pointer text-red-500 hover:text-red-700 ${className}`}
                >
                  LOGOUT
                </button>
              )}
            </div>
          ) : (
            // Show login button when not logged in
            <button
              onClick={handleLoginClick}
              className={`${
                isMobile ? "block py-2 w-full text-left border-b border-gray-200 " : ""
              }text-sm flex items-center font-medium cursor-pointer text-gray-500 hover:!text-[#F05A24] ${className}`}
            >
              LOGIN
            </button>
          )
        ) : (
          // Loading state during SSR/hydration
          <div
            className={`${
              isMobile ? "block py-2 border-b border-gray-200 " : ""
            }text-sm font-medium text-gray-500 animate-pulse`}
          >
            <div className="h-4 bg-gray-300 rounded w-16"/>
          </div>
        )}
      </div>

      {/* Login/Profile Completion Modal */}
      {isModalOpen && (
        <LoginFlowModal
          setModalOpen={setIsModalOpen}
          setLoginProfileBtnText={() => {}}
          setUserData={() => {}}
          isBooking={false}
          forceDetailsPage={isLoggedIn}
        />
      )}
    </>
  );
};

export default DynamicNavItems;
